import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

/**
 * Modal that plays the BBC Radio Humberside interview audio file.
 * Closes on overlay click, ESC key, or the X button. Locks body scroll while open.
 *
 * Props:
 *  - open: boolean - controls visibility
 *  - onClose: () => void - called when the user dismisses the modal
 *  - audioSrc?: string - optional override for the MP3 URL
 */
export default function BBCAudioModal({ open, onClose, audioSrc = '/bbc-humberside-bioturm.mp3' }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      data-testid="bbc-audio-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bbc-modal-title"
    >
      <button
        type="button"
        aria-label="Close interview"
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
        data-testid="bbc-modal-overlay"
      />
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.25 }}
        className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden"
      >
        <div className="bg-stone-900 text-white px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center bg-white text-stone-900 text-xs font-black tracking-[0.18em] px-2 py-1 rounded-sm">BBC</span>
            <div>
              <p id="bbc-modal-title" className="font-bold text-lg leading-tight">Radio Humberside</p>
              <p className="text-[12px] text-white/70">Interview &mdash; IIY Phytoventra</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white/70 hover:text-white transition-colors"
            aria-label="Close"
            data-testid="bbc-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-stone-600 mb-4">
            A live conversation with the founders on bringing clinically validated, gold-standard curcumin research from laboratory to UK households.
          </p>
          <audio controls preload="metadata" className="w-full" data-testid="bbc-audio-player">
            <source src={audioSrc} type="audio/mpeg" />
            Your browser does not support audio playback.
          </audio>
          <p className="text-[11px] text-stone-400 mt-4">© BBC Radio Humberside &middot; Reproduced with permission for member-credibility purposes.</p>
        </div>
      </motion.div>
    </div>
  );
}
